// api/finance.service.ts
import {
  IBankTransaction,
  IFinanceStats,
  IResidualVatTransactions,
  ITransactionCategory,
  ITransactionRule,
  IVatSummary,
} from "../@types/finance";
import axios from "../utils/axios";

export class FinanceService {
  private readonly base = "/finance";

  // ── Transactions ──
  getTransactions(params?: {
    month?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
    page?: number;
    direction?: string;
    status?: string;
  }) {
    return axios.get<IBankTransaction[]>(`${this.base}/transactions`, {
      params,
    });
  }

  getTransaction(id: string) {
    return axios.get<IBankTransaction>(`${this.base}/transactions/${id}`);
  }
  assignCategory(transactionId: string, categoryId: string, reason: string) {
    return axios.patch(`${this.base}/transactions/${transactionId}/category`, {
      categoryId: Number(categoryId),
      reason,
    });
  }
  getTransactionAudit(id: string) {
    return axios.get(`${this.base}/transactions/${id}/audit`);
  }

  // ✅ NEW: Mark a single transaction as manually reviewed (reconciled)
  markTransactionReviewed(id: string) {
    return axios.patch(`${this.base}/transactions/${id}/reconcile`);
  }

  // ── Categories (existing) ─────────────────────────────────────────────────
  getCategories() {
    return axios.get(`${this.base}/categories`);
  }
  createCategory(payload: Partial<ITransactionCategory>) {
    return axios.post(`${this.base}/categories`, payload);
  }
  updateCategory(id: string, payload: Partial<ITransactionCategory>) {
    return axios.patch(`${this.base}/categories/${id}`, payload);
  }

  // ── Rules (existing) ──────────────────────────────────────────────────────
  getRules() {
    return axios.get<ITransactionRule[]>(`${this.base}/rules`);
  }
  createRule(payload: Partial<ITransactionRule>) {
    return axios.post(`${this.base}/rules`, payload);
  }
  updateRulePriority(id: string, priority: number) {
    return axios.patch(`${this.base}/rules/${id}`, { priority });
  }
  updateRuleStatus(id: string, isActive: boolean) {
    return axios.patch(`${this.base}/rules/${id}`, { isActive });
  }
  deleteRule(id: string) {
    return axios.delete(`${this.base}/rules/${id}`);
  }

  // ── Stats ─────────────────────────────────────────────────────────────────
  getFinanceStats(params?: { month?: string; dateFrom?: string; dateTo?: string }) {
    return axios.get<IFinanceStats>(`${this.base}/stats`, { params });
  }

  getAvailableYears() {
    return axios.get<number[]>(`${this.base}/stats/years`);
  }

  // ── VAT ───────────────────────────────────────────────────────────────────
  // ✅ UPDATED: accepts quarter string directly (e.g. "Q3_2022")
  getVatSummary(quarter?: string) {
    return axios.get<IVatSummary>(`${this.base}/vat/summary`, {
      params: { quarter },
    });
  }
  getResidualVatTransactions(quarter?: string) {
    return axios.get<IResidualVatTransactions>(
      `${this.base}/vat/residual-transactions`,
      { params: { quarter } },
    );
  }
  postPeAdjustmentJournal(quarter: string) {
    return axios.post(`${this.base}/vat/pe-adjustment`, { quarter });
  }
  generateVat100(quarter: string): Promise<Blob> {
    return axios
      .get(`${this.base}/vat/vat100`, { params: { quarter }, responseType: "blob" })
      .then((r) => r.data);
  }
  generatePeWorkingPapers(quarter: string): Promise<Blob> {
    return axios
      .get(`${this.base}/vat/pe-working-papers`, { params: { quarter }, responseType: "blob" })
      .then((r) => r.data);
  }

  // ── Bank feed ─────────────────────────────────────────────────────────────
  initiateBankFeedOAuth(): Promise<{ redirectUrl: string }> {
    return axios.post(`${this.base}/bank/connect`).then((r) => r.data);
  }
  getConnectedAccounts() {
    return axios.get(`${this.base}/bank/accounts`);
  }
  triggerFeedSync(accountId?: number) {
    return axios.post(`${this.base}/bank/sync`, { accountId });
  }

  // ── Import ────────────────────────────────────────────────────────────────
  importStatement(formData: FormData) {
    return axios.post(`${this.base}/import`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  // ── Reconciliation ────────────────────────────────────────────────────────
  getReconciliationBatches() {
    return axios.get(`${this.base}/reconciliation/batches`);
  }
  // ✅ NEW: Create a batch (before reconciling)
  createReconciliationBatch(payload: {
    periodStart: string;
    periodEnd: string;
  }) {
    return axios.post(`${this.base}/reconciliation/batches`, payload);
  }
  reconcileBatch(batchId: string) {
    return axios.post(
      `${this.base}/reconciliation/batches/${batchId}/reconcile`,
    );
  }

  // ── Expenses (NEW) ────────────────────────────────────────────────────────
  getExpenses(params?: Record<string, any>) {
    return axios.get(`${this.base}/expenses`, { params });
  }
  createExpense(payload: {
    date: string;
    description: string;
    category: string;
    amount: number;
    vatCode?: string;
    reference?: string;
  }) {
    return axios.post(`${this.base}/expenses`, payload);
  }
  updateExpense(id: string, payload: any) {
    return axios.patch(`${this.base}/expenses/${id}`, payload);
  }
  deleteExpense(id: string) {
    return axios.delete(`${this.base}/expenses/${id}`);
  }

  // ── Exports ───────────────────────────────────────────────────────────────
  exportToXero() {
    return axios.post(`${this.base}/exports/xero`);
  }
  getExportHistory() {
    return axios.get(`${this.base}/exports/history`);
  }
  generatePlReport(params: {
    dateFrom: string;
    dateTo: string;
    branchId?: number;
  }) {
    return axios.get(`${this.base}/reports/pl`, { params });
  }
}
